import { useEffect, useRef, useState } from "react";
import { Heart, Loader2, Play, Mail, Brain, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const VIBES = [
  { id: "boyfriend", label: "Supportive boyfriend 💙", blurb: "Caring, affectionate, cheers you on" },
  { id: "bestie", label: "Bestie hype ✨", blurb: "Bubbly best-friend energy" },
  { id: "calm", label: "Calm & grounding 🌙", blurb: "Soft, steady, reassuring" },
  { id: "playful", label: "Playful & flirty 😉", blurb: "Fun, teasing, wholesome" },
];

export const NishuSettings = () => {
  const { org } = useAuth();
  const [voices, setVoices] = useState([]);
  const [voice, setVoice] = useState("");
  const [vibe, setVibe] = useState(org?.nishu_vibe || "boyfriend");
  const [hype, setHype] = useState(org?.nishu_hype_enabled !== false);
  const [hypeHour, setHypeHour] = useState(org?.nishu_hype_hour_utc ?? 8);
  const [voiceNote, setVoiceNote] = useState(org?.nishu_voice_note_enabled !== false);
  const [memories, setMemories] = useState([]);
  const [sendingHype, setSendingHype] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/voice/voices").then(({ data }) => {
      setVoices(data.voices || []);
      setVoice((c) => c || org?.nishu_voice || "IKne3meq5aSn9XLyUdCD");
    }).catch(() => {});
    api.get("/nishu/memory").then(({ data }) => setMemories(data.facts || [])).catch(() => {});
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, [org]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/aria", { nishu_voice: voice, nishu_vibe: vibe, nishu_hype_enabled: hype, nishu_hype_hour_utc: hypeHour, nishu_voice_note_enabled: voiceNote });
      toast.success("Nishu updated 💗");
    } catch { toast.error("Only admins can update Nishu"); }
    finally { setBusy(false); }
  };

  const sendHype = async () => {
    setSendingHype(true);
    try { const { data } = await api.post("/nudge/send-hype"); toast.success(`Hype note sent to ${data.to} 💌`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send hype email"); }
    finally { setSendingHype(false); }
  };

  const clearMemory = async () => {
    try { await api.delete("/nishu/memory"); setMemories([]); toast.success("Nishu cleared what she remembered"); }
    catch { toast.error("Could not clear memory"); }
  };

  const preview = async () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/tts", { text: "Hey you, it's Nishu. I've got you — let's crush today together.", voice }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = new Audio(url); audioRef.current = a;
      a.onended = () => { URL.revokeObjectURL(url); setPreviewing(false); };
      a.onerror = () => { URL.revokeObjectURL(url); setPreviewing(false); };
      await a.play();
    } catch { toast.error("Preview failed"); setPreviewing(false); }
  };

  const free = voices.filter((v) => v.category === "free");
  const premium = voices.filter((v) => v.category === "premium");

  return (
    <section className="aria-card p-5 mb-6" data-testid="nishu-settings-section">
      <div className="flex items-center gap-2 mb-1"><Heart size={16} className="text-rose-400" fill="currentColor" /><h2 className="text-base font-medium">Nishu — your companion</h2></div>
      <p className="text-xs text-[var(--aria-text-muted)] mb-4">Choose how your floating companion sounds and feels — kept separate from ARIA's voice.</p>

      <label className="text-xs text-[var(--aria-text-muted)]">Nishu's vibe</label>
      <div className="grid sm:grid-cols-2 gap-2 mt-1 mb-4">
        {VIBES.map((v) => (
          <button key={v.id} onClick={() => setVibe(v.id)} data-testid={`nishu-vibe-${v.id}`}
            className={`text-left rounded-lg border p-2.5 transition-colors ${vibe === v.id ? "border-rose-500 bg-rose-500/10" : "border-[var(--aria-border)] hover:border-rose-500/50"}`}>
            <div className="text-sm font-medium">{v.label}</div>
            <div className="text-[11px] text-[var(--aria-text-muted)]">{v.blurb}</div>
          </button>
        ))}
      </div>

      <label className="text-xs text-[var(--aria-text-muted)]" htmlFor="nishu-voice">Nishu's voice</label>
      <select id="nishu-voice" value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="nishu-voice-select"
        className="w-full mt-1 mb-4 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-sm">
        <optgroup label="Included voices">
          {free.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name} — {v.description}</option>)}
        </optgroup>
        <optgroup label="Premium (upgrade ElevenLabs to unlock)">
          {premium.map((v) => <option key={v.voice_id} value={v.voice_id} disabled={v.locked}>{v.name}{v.locked ? " 🔒" : ""} — {v.description}</option>)}
        </optgroup>
      </select>

      <div className="rounded-lg border border-[var(--aria-border)] p-3 mb-4">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <span className="flex items-center gap-2 text-sm"><Mail size={14} className="text-rose-400" /> Morning hype by email</span>
          <input type="checkbox" checked={hype} onChange={(e) => setHype(e.target.checked)} data-testid="nishu-hype-toggle" className="accent-rose-500 w-4 h-4" />
        </label>
        <p className="text-[11px] text-[var(--aria-text-muted)] mt-1.5">Nishu sends you her own sweet hype note each day — with extra love when your interview is near.</p>
        {hype && (
          <div className="mt-3 space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-[var(--aria-text-muted)]">Send time</span>
              <select value={hypeHour} onChange={(e) => setHypeHour(parseInt(e.target.value))} data-testid="nishu-hype-hour"
                className="px-2.5 py-1.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-bg)] outline-none text-xs">
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00 UTC</option>)}
              </select>
            </div>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="text-xs text-[var(--aria-text-muted)]">Attach a spoken voice clip 🎧</span>
              <input type="checkbox" checked={voiceNote} onChange={(e) => setVoiceNote(e.target.checked)} data-testid="nishu-voicenote-toggle" className="accent-rose-500 w-4 h-4" />
            </label>
          </div>
        )}
        <button onClick={sendHype} disabled={sendingHype} data-testid="nishu-send-hype-button"
          className="mt-3 text-xs text-rose-400 hover:underline inline-flex items-center gap-1.5 disabled:opacity-50">
          {sendingHype ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send me one now
        </button>
      </div>

      <div className="rounded-lg border border-[var(--aria-border)] p-3 mb-4" data-testid="nishu-memory-block">
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <span className="flex items-center gap-2 text-sm"><Brain size={14} className="text-rose-400" /> What Nishu remembers</span>
          {memories.length > 0 && (
            <button onClick={clearMemory} data-testid="nishu-memory-clear" className="text-[11px] text-[var(--aria-text-muted)] hover:text-rose-400 inline-flex items-center gap-1"><Trash2 size={11} /> Clear</button>
          )}
        </div>
        {memories.length === 0 ? (
          <p className="text-[11px] text-[var(--aria-text-muted)]">Nothing yet — as you chat, Nishu quietly remembers little things about you to make her notes feel personal.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {memories.map((m, i) => <span key={i} data-testid={`nishu-memory-${i}`} className="aria-pill text-[11px] bg-rose-500/10 border border-rose-500/25">{m}</span>)}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy || !voice} data-testid="nishu-save-button"
          className="btn btn-primary px-4 py-2 text-sm disabled:opacity-50">Save Nishu</button>
        <button onClick={preview} disabled={previewing || !voice} data-testid="nishu-preview-button"
          className="btn px-4 py-2 rounded-md border border-[var(--aria-border)] hover:border-rose-500/60 text-sm flex items-center gap-2 disabled:opacity-50">
          {previewing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Preview voice
        </button>
      </div>
    </section>
  );
};
